import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Users, UserPlus, Shield, ShieldAlert, Key, Globe, Search, CheckCircle2, AlertTriangle, Clock, RefreshCw, X, ChevronRight, Building2, Lock, Check, UserCheck, AlertCircle, Trash2, Award, Briefcase, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import { useAgentManagementStore, AgentProfile, AgentTeamMembership, EligibleEmployee, SupplierOrg, TeamRoleDef } from '../../../store/agentManagementStore';
import { useSupportSettingsStore } from '../../../store/supportSettingsStore';
import { useAuthStore } from '../../../store/authStore';

type TabView = 'internal' | 'external' | 'inactive' | 'matrix' | 'roles' | 'audit';

export function formatTeamName(name?: string | null): string {
  if (!name) return '';
  return String(name).replace(/\bDesk\b/gi, 'Team');
}

export function isTeamMatch(membershipTeam: AgentTeamMembership, queueTeam: any): boolean {
  const mId = String(membershipTeam.teamId || '');
  const mUuid = String(membershipTeam.teamUuid || '');
  const qId = String(queueTeam.id || queueTeam.team_id || '');
  const qUuid = String(queueTeam.uuid || '');

  if (mId && qId && (mId === qId || mId === String(queueTeam.id) || mId === String(queueTeam.team_id))) return true;
  if (mUuid && (mUuid === qId || mUuid === qUuid)) return true;
  if (qUuid && (mId === qUuid || mUuid === qUuid)) return true;

  const mKey = String(membershipTeam.teamKey || '').toLowerCase();
  const qKey = String(queueTeam.team_key || queueTeam.queue_key || queueTeam.code || queueTeam.key || '').toLowerCase();
  if (mKey && qKey) {
    if (mKey === qKey) return true;
    if ((mKey === 'sd' || mKey === 'support_desk') && (qKey === 'support' || qKey === 'sd')) return true;
    if ((mKey === 'td' || mKey === 'technical_desk') && (qKey === 'technical' || qKey === 'td')) return true;
    if (mKey === 'sw' && (qKey === 'software' || qKey === 'sw')) return true;
    if (mKey === 'sup' && (qKey === 'supplier' || qKey === 'sup')) return true;
    if (mKey === 'mgt' && (qKey === 'management' || qKey === 'mgt')) return true;
    if (mKey === 'hr' && qKey === 'hr') return true;
  }

  const mName = String(membershipTeam.teamName || '').toLowerCase().replace(/\b(team|desk)\b/gi, '').trim();
  const qName = String(queueTeam.team_name || queueTeam.queue_label || queueTeam.label || queueTeam.name || '').toLowerCase().replace(/\b(team|desk)\b/gi, '').trim();
  if (mName && qName && mName === qName) return true;

  return false;
}

export function AgentsAndAccessView({
  onRegisterHeaderActions,
}: {
  onRegisterHeaderActions?: (actions: React.ReactNode) => void;
} = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuthStore();
  const {
    agents,
    eligibleEmployees,
    supplierOrgs,
    roles,
    accessMatrix,
    auditLogs,
    currentAgent,
    isLoading,
    fetchAgents,
    fetchEligibleEmployees,
    fetchSupplierOrgs,
    fetchRoles,
    fetchAccessMatrix,
    fetchAuditLogs,
    fetchCurrentAgent,
    createInternalAgent,
    createExternalAgent,
    createSupplierOrg,
    updateAgent,
    setAgentTeams,
    suspendAgent,
    reactivateAgent,
    deactivateAgent,
    resendInvite,
    updateExpiry,
    updateRolePermissions,
  } = useAgentManagementStore();

  const { queues, fetchQueues } = useSupportSettingsStore();

  // Active top tab
  const requestedAgentTab = searchParams.get('agentTab') as TabView | null;
  const [activeTab, setActiveTab] = useState<TabView>(
    requestedAgentTab && ['internal', 'external', 'inactive', 'matrix', 'roles', 'audit'].includes(requestedAgentTab)
      ? requestedAgentTab
      : 'internal'
  );
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTeamFilter, setSelectedTeamFilter] = useState<string>('all');
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<string>('all');
  const [selectedDeptFilter, setSelectedDeptFilter] = useState<string>('all');

  // Modals & Drawers
  const [showAddInternalModal, setShowAddInternalModal] = useState(false);
  const [showAddExternalModal, setShowAddExternalModal] = useState(false);
  const [selectedAgentForDrawer, setSelectedAgentForDrawer] = useState<AgentProfile | null>(null);
  const [deactivatingAgent, setDeactivatingAgent] = useState<AgentProfile | null>(null);
  const [suspendingAgent, setSuspendingAgent] = useState<AgentProfile | null>(null);
  const [suspendReason, setSuspendReason] = useState('');
  const [deactivateReason, setDeactivateReason] = useState('');
  const [deactivateReassignMode, setDeactivateReassignMode] = useState<'queue' | 'user'>('queue');
  const [deactivateTargetUserId, setDeactivateTargetUserId] = useState('');

  // Initial load
  useEffect(() => {
    fetchAgents();
    fetchQueues();
    fetchRoles();
    fetchSupplierOrgs();
    fetchCurrentAgent();
  }, [fetchAgents, fetchQueues, fetchRoles, fetchSupplierOrgs, fetchCurrentAgent]);

  useEffect(() => {
    const next = searchParams.get('agentTab') as TabView | null;
    if (next && next !== activeTab && ['internal', 'external', 'inactive', 'matrix', 'roles', 'audit'].includes(next)) {
      setActiveTab(next);
    }
  }, [searchParams, activeTab]);

  const selectAgentTab = (tab: TabView) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('agentTab', tab);
    setSearchParams(next);
    if (tab === 'matrix') fetchAccessMatrix();
    if (tab === 'audit') fetchAuditLogs();
    if (tab === 'roles') fetchRoles();
  };

  // Auth & Permissions check
  const isSuperAdmin = useMemo(() => {
    if (user?.roles?.some((r: any) => r.code === 'super_admin' || r === 'super_admin' || r?.name === 'Super Admin')) {
      return true;
    }
    return currentAgent?.isSuperAdmin || currentAgent?.permissions?.includes('agents.grant_admin') || false;
  }, [user, currentAgent]);

  const canManageAgents = useMemo(() => {
    if (isSuperAdmin) return true;
    return (
      currentAgent?.profile?.accessLevel === 'admin' ||
      currentAgent?.permissions?.includes('agents.manage') ||
      false
    );
  }, [isSuperAdmin, currentAgent]);

  useEffect(() => {
    if (!onRegisterHeaderActions) return;
    onRegisterHeaderActions(
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => {
            fetchAgents();
            fetchQueues();
            fetchRoles();
            toast.success('Agent directory synchronized');
          }}
          className="inline-flex h-8 items-center gap-1.5 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-secondary transition-colors cursor-pointer"
        >
          <RefreshCw className={cn("size-3.5 text-muted-foreground", isLoading && "animate-spin")} />
          <span>Refresh</span>
        </button>

        {canManageAgents && (
          <>
            <button
              type="button"
              onClick={() => setShowAddExternalModal(true)}
              className="inline-flex h-8 items-center gap-1.5 px-3 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-secondary transition-colors cursor-pointer"
            >
              <Globe className="size-3.5 text-info" />
              <span>Add External User</span>
            </button>

            <button
              type="button"
              onClick={() => setShowAddInternalModal(true)}
              className="inline-flex h-8 items-center gap-1.5 px-3.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold shadow-xs hover:bg-primary/90 transition-all cursor-pointer"
            >
              <UserPlus className="size-3.5" />
              <span>Add Internal Agent</span>
            </button>
          </>
        )}
      </div>
    );
    return () => onRegisterHeaderActions(null);
  }, [canManageAgents, isLoading, onRegisterHeaderActions, fetchAgents, fetchQueues, fetchRoles]);

  // Departments list for filter
  const departments = useMemo(() => {
    const set = new Set<string>();
    agents.forEach((a) => {
      if (a.department) set.add(a.department);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [agents]);

  const activeAdminCount = useMemo(() => {
    return agents.filter((a) => a.status === 'active' && a.accessLevel === 'admin').length;
  }, [agents]);

  // Filtered agents
  const filteredAgents = useMemo(() => {
    return agents.filter((agent) => {
      // Tab filter
      if (activeTab === 'internal') {
        if (agent.kind !== 'internal' || agent.status === 'suspended' || agent.status === 'inactive') {
          return false;
        }
        if (agent.accessLevel !== 'admin' && agent.teams.length === 0) {
          return false;
        }
      }
      if (activeTab === 'external' && (agent.kind !== 'external' || agent.status === 'suspended' || agent.status === 'inactive')) {
        return false;
      }
      if (activeTab === 'inactive' && agent.status !== 'suspended' && agent.status !== 'inactive') {
        return false;
      }

      // Search query
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const fullName = `${agent.firstName} ${agent.lastName}`.toLowerCase();
        const matchesName = fullName.includes(q);
        const matchesEmail = agent.email.toLowerCase().includes(q);
        const matchesDept = (agent.department || '').toLowerCase().includes(q);
        const matchesDesig = (agent.designation || '').toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesDept && !matchesDesig) return false;
      }

      // Team filter
      if (selectedTeamFilter !== 'all') {
        const inTeam = agent.teams.some((t) => t.teamId === selectedTeamFilter || t.teamKey === selectedTeamFilter);
        if (!inTeam) return false;
      }

      // Role filter
      if (selectedRoleFilter !== 'all') {
        if (agent.accessLevel !== selectedRoleFilter && !agent.teams.some((t) => t.teamRoleCode === selectedRoleFilter)) {
          return false;
        }
      }

      // Department filter
      if (selectedDeptFilter !== 'all' && agent.department !== selectedDeptFilter) {
        return false;
      }

      return true;
    });
  }, [agents, activeTab, searchQuery, selectedTeamFilter, selectedRoleFilter, selectedDeptFilter]);

  // Trigger Suspend
  const handleExecuteSuspension = async () => {
    if (!suspendingAgent) return;
    if (suspendingAgent.status === 'active' && suspendingAgent.accessLevel === 'admin' && activeAdminCount <= 1) {
      toast.error('Cannot suspend the only active Support Admin. Please assign another active agent as Support Admin first.');
      return;
    }
    const finalReason = suspendReason.trim() || 'Administrative suspension';
    const agentName = `${suspendingAgent.firstName || ''} ${suspendingAgent.lastName || ''}`.trim() || suspendingAgent.email || 'Agent';
    try {
      await suspendAgent(suspendingAgent.id, finalReason);
      toast.success(`Agent ${agentName} access suspended`);
      setSuspendingAgent(null);
      setSuspendReason('');
      if (selectedAgentForDrawer?.id === suspendingAgent.id) {
        setSelectedAgentForDrawer(null);
      }
      await fetchAgents();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || err.message || 'Suspension failed');
    }
  };

  // Trigger Deactivate
  const handleExecuteDeactivation = async () => {
    if (!deactivatingAgent) return;
    if (deactivatingAgent.status === 'active' && deactivatingAgent.accessLevel === 'admin' && activeAdminCount <= 1) {
      toast.error('Cannot deactivate the only active Support Admin. Please assign another active agent as Support Admin first.');
      return;
    }
    const finalReason = deactivateReason.trim() || 'Role departure / contract concluded';
    if (deactivateReassignMode === 'user' && !deactivateTargetUserId) {
      toast.error('Please select an active agent for ticket reassignment');
      return;
    }
    const agentName = `${deactivatingAgent.firstName || ''} ${deactivatingAgent.lastName || ''}`.trim() || deactivatingAgent.email || 'Agent';
    try {
      await deactivateAgent(deactivatingAgent.id, finalReason, {
        mode: deactivateReassignMode,
        userId: deactivateTargetUserId || undefined,
      });
      toast.success(`Agent ${agentName} deactivated`);
      setDeactivatingAgent(null);
      setDeactivateReason('');
      setDeactivateTargetUserId('');
      if (selectedAgentForDrawer?.id === deactivatingAgent.id) {
        setSelectedAgentForDrawer(null);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || err.message || 'Deactivation failed');
    }
  };

  const [togglingAgentTeam, setTogglingAgentTeam] = useState<string | null>(null);
  const [togglingRolePerm, setTogglingRolePerm] = useState<string | null>(null);

  const handleToggleRolePermission = async (role: TeamRoleDef, permCode: string, currentlyGranted: boolean) => {
    if (!isSuperAdmin) return;
    const toggleKey = `${role.id}-${permCode}`;
    setTogglingRolePerm(toggleKey);
    const activeCodes = (role.permissions || []).filter((p) => p.granted).map((p) => p.code);
    const updatedCodes = currentlyGranted
      ? activeCodes.filter((c) => c !== permCode)
      : [...activeCodes, permCode];
    try {
      await updateRolePermissions(role.id, updatedCodes);
      toast.success(`Updated permission ${permCode} for ${role.name}`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to update permission');
    } finally {
      setTogglingRolePerm(null);
    }
  };

  const handleToggleTeam = async (agent: AgentProfile, team: any, membership: AgentTeamMembership | null | undefined) => {
    if (!canManageAgents) return;

    const toggleKey = `${agent.id}-${team.id || team.team_id || team.queue_key}`;
    setTogglingAgentTeam(toggleKey);

    const teamIdentifier = String(team.id || team.team_id || team.uuid);
    const teamDisplayName = formatTeamName(team.team_name || team.queue_label || team.label || team.name);

    try {
      if (membership) {
        // Remove from team
        const updatedTeams = agent.teams
          .filter((t) => !isTeamMatch(t, team))
          .map((t) => ({
            teamId: t.teamId,
            teamRoleCode: t.teamRoleCode || 'agent',
            isPrimary: t.isPrimary,
          }));
        await setAgentTeams(agent.id, updatedTeams);
        toast.success(`Removed ${agent.firstName || 'Agent'} from ${teamDisplayName}`);
      } else {
        // Assign to team
        const updatedTeams = [
          ...agent.teams.map((t) => ({
            teamId: t.teamId,
            teamRoleCode: t.teamRoleCode || 'agent',
            isPrimary: t.isPrimary,
          })),
          {
            teamId: teamIdentifier,
            teamRoleCode: 'agent',
            isPrimary: agent.teams.length === 0,
          },
        ];
        await setAgentTeams(agent.id, updatedTeams);
        toast.success(`Assigned ${agent.firstName || 'Agent'} to ${teamDisplayName}`);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update team assignment');
    } finally {
      setTogglingAgentTeam(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="rounded-lg border border-border bg-card p-5 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary border border-primary/20">
              <Shield className="size-3" /> RBAC Engine Active
            </span>
          </div>

          {!onRegisterHeaderActions && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                fetchAgents();
                fetchQueues();
                fetchRoles();
                toast.success('Agent directory synchronized');
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <RefreshCw className={cn("size-3.5 text-muted-foreground", isLoading && "animate-spin")} />
              <span>Refresh</span>
            </button>

            {canManageAgents && (
              <>
                <button
                  type="button"
                  onClick={() => setShowAddExternalModal(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
                >
                  <Globe className="size-3.5 text-info" />
                  <span>Add External User</span>
                </button>

                <button
                  type="button"
                  onClick={() => setShowAddInternalModal(true)}
                  className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold shadow-xs hover:bg-primary/90 transition-all cursor-pointer"
                >
                  <UserPlus className="size-3.5" />
                  <span>Add Internal Agent</span>
                </button>
              </>
            )}
          </div>
          )}
        </div>

        {/* Tab navigation */}
        <div className="mt-3 flex h-11 items-center gap-4 overflow-x-auto border-b border-border px-1 select-none scrollbar-none" role="tablist" aria-label="Agents and access views">
          {[
            { id: 'internal', label: `Internal Agents (${agents.filter((a) => a.kind === 'internal' && a.status !== 'suspended' && a.status !== 'inactive' && (a.accessLevel === 'admin' || a.teams.length > 0)).length})`, icon: Users },
            { id: 'external', label: `External Suppliers (${agents.filter((a) => a.kind === 'external' && a.status !== 'suspended' && a.status !== 'inactive').length})`, icon: Globe },
            { id: 'inactive', label: `Suspended & Inactive (${agents.filter((a) => a.status === 'suspended' || a.status === 'inactive').length})`, icon: Clock },
            { id: 'matrix', label: 'Teams & Roles Matrix', icon: Award },
            { id: 'roles', label: 'Role Definitions', icon: Key },
            { id: 'audit', label: 'Access Audit Log', icon: ShieldAlert },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls="agents-access-panel"
                tabIndex={active ? 0 : -1}
                onClick={() => selectAgentTab(tab.id as TabView)}
                onKeyDown={(event) => {
                  const tabs: TabView[] = ['internal', 'external', 'inactive', 'matrix', 'roles', 'audit'];
                  const current = tabs.indexOf(activeTab);
                  let next = current;
                  if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
                  else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
                  else if (event.key === 'Home') next = 0;
                  else if (event.key === 'End') next = tabs.length - 1;
                  else return;
                  event.preventDefault();
                  const tabList = event.currentTarget.parentElement;
                  selectAgentTab(tabs[next]);
                  requestAnimationFrame(() => tabList?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus());
                }}
                className={cn(
                  "inline-flex h-11 shrink-0 items-center gap-1.5 border-b-2 px-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20",
                  active
                    ? "-mb-px border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="size-3.5" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* VIEW: AGENTS DIRECTORY (Internal, External, or Inactive) */}
      <div id="agents-access-panel" role="tabpanel" tabIndex={0}>
      {(activeTab === 'internal' || activeTab === 'external' || activeTab === 'inactive') && (
        <div className="space-y-4">
          {/* Filters Bar */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-card p-3 rounded-lg border border-border">
            <div className="flex flex-1 items-center gap-2 max-w-md">
              <div className="relative w-full">
                <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search by name, email, department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 text-xs bg-background border border-input rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={selectedTeamFilter}
                onChange={(e) => setSelectedTeamFilter(e.target.value)}
                className="text-xs bg-background border border-input rounded-md px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">All Teams</option>
                {queues.map((q) => (
                  <option key={q.id || q.queue_key} value={q.team_id || q.id}>
                    {formatTeamName(q.team_name || q.queue_label || q.label)}
                  </option>
                ))}
              </select>

              <select
                value={selectedRoleFilter}
                onChange={(e) => setSelectedRoleFilter(e.target.value)}
                className="text-xs bg-background border border-input rounded-md px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
              >
                <option value="all">All Roles</option>
                <option value="admin">System Admin</option>
                <option value="member">Team Member</option>
                <option value="team_lead">Team Lead</option>
                <option value="team_manager">Team Manager</option>
                <option value="agent">Support Agent</option>
              </select>

              {departments.length > 0 && (
                <select
                  value={selectedDeptFilter}
                  onChange={(e) => setSelectedDeptFilter(e.target.value)}
                  className="text-xs bg-background border border-input rounded-md px-2.5 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  <option value="all">All Departments</option>
                  {departments.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              )}

              {(searchQuery || selectedTeamFilter !== 'all' || selectedRoleFilter !== 'all' || selectedDeptFilter !== 'all') && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTeamFilter('all');
                    setSelectedRoleFilter('all');
                    setSelectedDeptFilter('all');
                  }}
                  className="text-xs text-muted-foreground hover:text-foreground px-2 py-1 underline cursor-pointer"
                >
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-border bg-card overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                  <tr className="h-10">
                    <th className="py-2.5 px-4 font-display">Agent / User</th>
                    <th className="py-2.5 px-4 font-display">Department / Org</th>
                    <th className="py-2.5 px-4 font-display">Access Level</th>
                    <th className="py-2.5 px-4 font-display">Assigned Teams &amp; Roles</th>
                    <th className="py-2.5 px-4 font-display">Status</th>
                    <th className="py-2.5 px-4 font-display">Last Active</th>
                    <th className="py-2.5 px-4 text-right font-display">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border font-sans">
                  {filteredAgents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-muted-foreground">
                        <Users className="size-8 mx-auto text-muted-foreground/40 mb-2" />
                        <p className="font-medium">No agents found</p>
                        <p className="text-xs mt-0.5">Try adjusting your search criteria or add a new agent.</p>
                      </td>
                    </tr>
                  ) : (
                    filteredAgents.map((agent) => {
                      return (
                        <tr
                          key={agent.id}
                          onClick={() => setSelectedAgentForDrawer(agent)}
                          className="h-12 cursor-pointer transition-colors hover:bg-muted/30"
                        >
                          {/* Name & Email */}
                          <td className="py-2.5 px-4">
                            <div className="flex items-center gap-2.5">
                              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                {((agent.firstName?.[0] || agent.email?.[0] || 'A')).toUpperCase()}{agent.lastName?.[0]?.toUpperCase() || ''}
                              </div>
                              <div>
                                <div className="font-semibold text-foreground flex items-center gap-1.5">
                                  <span>{`${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || 'Agent'}</span>
                                  {agent.warningFlag === 'NO_EMPLOYEE_RECORD' && (
                                    <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] bg-warning/10 text-warning font-mono" title="No matching People employee found">
                                      System
                                    </span>
                                  )}
                                  {agent.kind === 'external' && (
                                    <span className="inline-flex items-center px-1.5 py-0.2 rounded text-[10px] bg-info/10 text-info font-medium">
                                      Supplier
                                    </span>
                                  )}
                                </div>
                                <div className="text-muted-foreground font-mono text-[11px]">
                                  {agent.email || '—'}
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Dept / Org */}
                          <td className="py-2.5 px-4 text-foreground">
                            {agent.kind === 'internal' ? (
                              <div>
                                <div className="font-medium">{agent.department || '—'}</div>
                                <div className="text-muted-foreground text-[11px]">{agent.designation || 'Staff'}</div>
                              </div>
                            ) : (
                              <div>
                                <div className="font-medium text-info flex items-center gap-1">
                                  <Building2 className="size-3" />
                                  <span>{agent.supplierOrgName || 'External Vendor'}</span>
                                </div>
                                {agent.accessExpiresAt && (
                                  <div className="text-muted-foreground text-[11px]">
                                    Exp: {new Date(agent.accessExpiresAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Access Level */}
                          <td className="py-2.5 px-4">
                            {agent.accessLevel === 'admin' ? (
                              <span
                                title="Support Admin access level — assigned by Super Admin in Admin > Users"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary"
                              >
                                <Shield className="size-3" /> Support Admin
                              </span>
                            ) : agent.accessLevel === 'external' ? (
                              <span
                                title="External Agent — supplier / vendor access"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-info/10 text-info"
                              >
                                <Globe className="size-3" /> External Agent
                              </span>
                            ) : (
                              <span
                                title="Member access level — assigned by Super Admin in Admin > Users"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground"
                              >
                                Member
                              </span>
                            )}
                          </td>

                          {/* Assigned Teams with Role Chips */}
                          <td className="py-2.5 px-4">
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {agent.teams.length === 0 ? (
                                <span className="text-muted-foreground text-xs italic">No teams assigned</span>
                              ) : (
                                agent.teams.map((t) => (
                                  <span
                                    key={`${agent.id}-${t.teamId || t.teamKey}-${t.teamKey}`}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-[11px] font-medium border border-border"
                                  >
                                    <span className="text-foreground">{formatTeamName(t.teamName)}</span>
                                    <span className="text-muted-foreground font-mono text-[10px]">
                                      ({t.teamRoleName || t.teamRoleCode || 'Agent'})
                                    </span>
                                    {t.isPrimary && (
                                      <span className="text-warning text-[10px]" title="Primary team">★</span>
                                    )}
                                  </span>
                                ))
                              )}
                            </div>
                          </td>

                          {/* Status */}
                          <td className="py-2.5 px-4">
                            {agent.status === 'active' && agent.isEffective ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-success/10 text-success">
                                <span className="size-1.5 rounded-full bg-success" /> Active
                              </span>
                            ) : agent.status === 'active' && !agent.isEffective ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-warning/10 text-warning" title="Inactive in People module or expired">
                                <AlertTriangle className="size-3" /> Non-Effective
                              </span>
                            ) : agent.status === 'invited' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-info/10 text-info">
                                <Clock className="size-3" /> Invited
                              </span>
                            ) : agent.status === 'suspended' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-warning/10 text-warning">
                                <AlertCircle className="size-3" /> Suspended
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-muted text-muted-foreground">
                                Inactive
                              </span>
                            )}
                          </td>

                          {/* Last Active */}
                          <td className="py-2.5 px-4 text-muted-foreground text-[11px]">
                            {agent.lastActiveAt ? new Date(agent.lastActiveAt).toLocaleString() : 'Never'}
                          </td>

                          {/* Actions */}
                          <td className="py-2.5 px-4 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1.5">
                              {canManageAgents && agent.status === 'active' && (
                                <>
                                  <button
                                    type="button"
                                    disabled={agent.accessLevel === 'admin' && activeAdminCount <= 1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAgentForDrawer(null);
                                      setSuspendingAgent(agent);
                                      setSuspendReason('');
                                    }}
                                    className={cn(
                                      "px-2 py-0.5 rounded text-[11px] font-medium border transition-colors",
                                      agent.accessLevel === 'admin' && activeAdminCount <= 1
                                        ? "border-border text-muted-foreground opacity-50 cursor-not-allowed"
                                        : "border-warning/50 text-warning hover:bg-warning/10 cursor-pointer"
                                    )}
                                    title={
                                      agent.accessLevel === 'admin' && activeAdminCount <= 1
                                        ? "Cannot suspend the only active Support Admin"
                                        : "Suspend Agent Access"
                                    }
                                  >
                                    Suspend
                                  </button>
                                  <button
                                    type="button"
                                    disabled={agent.accessLevel === 'admin' && activeAdminCount <= 1}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAgentForDrawer(null);
                                      setDeactivatingAgent(agent);
                                      setDeactivateReason('Role departure / contract concluded');
                                    }}
                                    className={cn(
                                      "px-2 py-0.5 rounded text-[11px] font-medium border transition-colors",
                                      agent.accessLevel === 'admin' && activeAdminCount <= 1
                                        ? "border-border text-muted-foreground opacity-50 cursor-not-allowed"
                                        : "border-destructive/50 text-destructive hover:bg-destructive/10 cursor-pointer"
                                    )}
                                    title={
                                      agent.accessLevel === 'admin' && activeAdminCount <= 1
                                        ? "Cannot deactivate the only active Support Admin"
                                        : "Deactivate Agent"
                                    }
                                  >
                                    Deactivate
                                  </button>
                                </>
                              )}
                              {canManageAgents && agent.status === 'suspended' && (
                                <>
                                  <button
                                    type="button"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        await reactivateAgent(agent.id);
                                        toast.success('Agent reactivated');
                                        await fetchAgents();
                                      } catch (err: any) {
                                        toast.error(err.response?.data?.message || err.message || 'Failed to reactivate agent');
                                      }
                                    }}
                                    className="px-2 py-0.5 rounded text-[11px] font-semibold bg-success/15 text-success hover:bg-success/25 transition-colors cursor-pointer"
                                    title="Reactivate Agent Access"
                                  >
                                    Reactivate
                                  </button>
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedAgentForDrawer(null);
                                      setDeactivatingAgent(agent);
                                      setDeactivateReason('Role departure / contract concluded');
                                    }}
                                    className="px-2 py-0.5 rounded text-[11px] font-medium border border-destructive/50 text-destructive hover:bg-destructive/10 transition-colors cursor-pointer"
                                    title="Deactivate Agent"
                                  >
                                    Deactivate
                                  </button>
                                </>
                              )}
                              {canManageAgents && agent.status === 'inactive' && (
                                <button
                                  type="button"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      await reactivateAgent(agent.id);
                                      toast.success('Agent reactivated');
                                      await fetchAgents();
                                    } catch (err: any) {
                                      toast.error(err.response?.data?.message || err.message || 'Failed to reactivate agent');
                                    }
                                  }}
                                  className="px-2.5 py-0.5 rounded text-[11px] font-semibold bg-success/15 text-success hover:bg-success/25 transition-colors cursor-pointer"
                                  title="Reactivate Agent Access"
                                >
                                  Reactivate
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setSelectedAgentForDrawer(agent)}
                                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer"
                                title="View & Edit Details"
                              >
                                <ChevronRight className="size-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* VIEW: TEAMS & ROLES MATRIX */}
      {activeTab === 'matrix' && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-foreground font-display text-card-title font-semibold">
                Team Membership &amp; Role Assignments Matrix
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Overview of all effective agents and their assigned support team roles.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2.5 text-xs text-muted-foreground mr-1">
                <div className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center size-5 rounded bg-success/15 border border-success/35 text-success">
                    <Check className="size-3.5 stroke-[2.75]" />
                  </span>
                  <span>Assigned</span>
                </div>
                <div className="inline-flex items-center gap-1.5">
                  <span className="inline-flex items-center justify-center size-5 rounded bg-destructive/10 border border-destructive/25 text-destructive">
                    <X className="size-3.5 stroke-[2.5]" />
                  </span>
                  <span>Unassigned</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  fetchAgents();
                  fetchQueues();
                  fetchAccessMatrix();
                }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                <RefreshCw className="size-3.5 text-muted-foreground" />
                <span>Refresh Matrix</span>
              </button>
            </div>
          </div>

          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                <tr className="h-10">
                  <th className="py-2.5 px-4 font-display sticky left-0 bg-muted/90 backdrop-blur-xs min-w-[200px]">
                    Agent Name
                  </th>
                  {queues.map((team) => (
                    <th key={team.id || team.queue_key} className="py-2.5 px-4 font-display text-center min-w-[140px]">
                      <div>{formatTeamName(team.team_name || team.queue_label || team.label)}</div>
                      <div className="text-[10px] font-normal text-muted-foreground font-mono">
                        {team.team_key || team.queue_key}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {agents.map((agent) => (
                  <tr key={agent.id} className="h-11 hover:bg-muted/30 transition-colors">
                    <td className="py-2.5 px-4 font-semibold text-foreground sticky left-0 bg-card">
                      <div className="flex items-center gap-2">
                        <span>{agent.firstName} {agent.lastName}</span>
                        {agent.accessLevel === 'admin' && (
                          <span className="text-[10px] text-primary bg-primary/10 px-1 rounded font-bold">Admin</span>
                        )}
                      </div>
                    </td>
                    {queues.map((team) => {
                      const membership = agent.teams.find((t) => isTeamMatch(t, team));
                      const isToggling = togglingAgentTeam === `${agent.id}-${team.id || team.team_id || team.queue_key}`;

                      return (
                        <td key={team.id || team.queue_key} className="py-2 px-4 text-center">
                          {membership ? (
                            <button
                              type="button"
                              disabled={!canManageAgents || isToggling}
                              onClick={() => handleToggleTeam(agent, team, membership)}
                              className={cn(
                                "inline-flex items-center justify-center size-7 rounded-md transition-all shadow-2xs",
                                "bg-success/15 border border-success/35 text-success hover:bg-success/25 hover:border-success/50",
                                !canManageAgents && "cursor-default opacity-90 hover:bg-success/15 hover:border-success/35",
                                isToggling && "opacity-50 animate-pulse"
                              )}
                              title={
                                canManageAgents
                                  ? `${membership.teamRoleName || membership.teamRoleCode || 'Member'}${membership.isPrimary ? ' ★ Primary' : ''} — Click to remove`
                                  : `${membership.teamRoleName || membership.teamRoleCode || 'Member'}${membership.isPrimary ? ' ★ Primary' : ''}`
                              }
                            >
                              <Check className="size-4 stroke-[2.75]" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              disabled={!canManageAgents || isToggling}
                              onClick={() => handleToggleTeam(agent, team, null)}
                              className={cn(
                                "inline-flex items-center justify-center size-7 rounded-md transition-all shadow-2xs",
                                "bg-destructive/10 border border-destructive/25 text-destructive hover:bg-destructive/20 hover:border-destructive/40",
                                !canManageAgents && "cursor-default opacity-80 hover:bg-destructive/10 hover:border-destructive/25",
                                isToggling && "opacity-50 animate-pulse"
                              )}
                              title={canManageAgents ? "Not assigned — Click to assign" : "Not assigned"}
                            >
                              <X className="size-4 stroke-[2.5]" />
                            </button>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* VIEW: ROLE DEFINITIONS */}
      {activeTab === 'roles' && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-foreground font-display text-card-title font-semibold">
                Support Team Roles &amp; Permissions
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Standard roles are system-defined and immutable in structure. Super Admin can adjust granted permissions.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isSuperAdmin && (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-muted text-muted-foreground text-xs font-semibold">
                  <Lock className="size-3" /> Read-Only Mode (Super Admin only can edit)
                </span>
              )}
              <button
                type="button"
                onClick={() => fetchRoles()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                <RefreshCw className="size-3.5 text-muted-foreground" />
                <span>Refresh Roles</span>
              </button>
            </div>
          </div>

          {roles.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground border border-dashed border-border rounded-lg">
              <Award className="size-8 mx-auto text-muted-foreground/40 mb-2" />
              <p className="font-medium">No roles loaded</p>
              <button
                type="button"
                onClick={() => fetchRoles()}
                className="mt-2 text-xs text-primary hover:underline cursor-pointer"
              >
                Click to reload role definitions
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
              {roles.map((role) => (
                <RoleCard
                  key={role.id}
                  role={role}
                  isSuperAdmin={isSuperAdmin}
                  onTogglePermission={handleToggleRolePermission}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* VIEW: ACCESS AUDIT LOG */}
      {activeTab === 'audit' && (
        <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-foreground font-display text-card-title font-semibold">
                Agent Access &amp; Role Audit Trail
              </h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                Append-only log of every permission grant, role mutation, suspension, or deactivation.
              </p>
            </div>
            <button
              type="button"
              onClick={() => fetchAuditLogs()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
            >
              <RefreshCw className="size-3.5 text-muted-foreground" />
              <span>Refresh Log</span>
            </button>
          </div>

          <div className="overflow-x-auto border border-border rounded-lg">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="border-b border-border bg-muted/40 font-semibold text-muted-foreground">
                <tr className="h-10">
                  <th className="py-2.5 px-4 font-display">Timestamp</th>
                  <th className="py-2.5 px-4 font-display">Actor</th>
                  <th className="py-2.5 px-4 font-display">Action</th>
                  <th className="py-2.5 px-4 font-display">Reason / Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-sans">
                {auditLogs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="py-8 text-center text-muted-foreground">
                      No audit events recorded yet.
                    </td>
                  </tr>
                ) : (
                  auditLogs.map((log) => (
                    <tr key={log.id} className="h-10 hover:bg-muted/30">
                      <td className="py-2.5 px-4 text-muted-foreground font-mono text-[11px]">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="py-2.5 px-4 font-medium text-foreground">
                        {log.actorName || 'System'}
                      </td>
                      <td className="py-2.5 px-4">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-semibold bg-muted text-foreground border border-border">
                          {log.action}
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-foreground">
                        {log.reason || '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </div>

      {/* DRAWER: AGENT DETAILS */}
      {selectedAgentForDrawer && (
        <AgentDetailDrawer
          agent={selectedAgentForDrawer}
          canManage={canManageAgents}
          isSuperAdmin={isSuperAdmin}
          queues={queues}
          roles={roles}
          onClose={() => setSelectedAgentForDrawer(null)}
          onRefresh={async () => {
            await fetchAgents();
            const freshAgents = useAgentManagementStore.getState().agents;
            const updated = freshAgents.find((a) => a.id === selectedAgentForDrawer.id);
            if (updated) setSelectedAgentForDrawer(updated);
          }}
          onSuspend={async (reason) => {
            if (selectedAgentForDrawer.status === 'active' && selectedAgentForDrawer.accessLevel === 'admin' && activeAdminCount <= 1) {
              toast.error('Cannot suspend the only active Support Admin. Please assign another active agent as Support Admin first.');
              return;
            }
            try {
              await suspendAgent(selectedAgentForDrawer.id, reason);
              toast.success('Agent access suspended');
              setSelectedAgentForDrawer(null);
              await fetchAgents();
            } catch (err: any) {
              toast.error(err.response?.data?.message || err.response?.data?.error || err.message || 'Suspension failed');
            }
          }}
          onReactivate={async () => {
            try {
              await reactivateAgent(selectedAgentForDrawer.id);
              toast.success('Agent access reactivated');
              setSelectedAgentForDrawer(null);
              await fetchAgents();
            } catch (err: any) {
              toast.error(err.response?.data?.message || err.response?.data?.error || err.message || 'Reactivation failed');
            }
          }}
          onStartDeactivate={() => {
            const target = selectedAgentForDrawer;
            setSelectedAgentForDrawer(null);
            setDeactivatingAgent(target);
            setDeactivateReason('Role departure / contract concluded');
          }}
        />
      )}

      {/* MODAL: ADD INTERNAL AGENT WIZARD */}
      {showAddInternalModal && (
        <AddInternalAgentWizard
          queues={queues}
          roles={roles}
          isSuperAdmin={isSuperAdmin}
          onClose={() => setShowAddInternalModal(false)}
          onSuccess={() => {
            setShowAddInternalModal(false);
            fetchAgents();
          }}
        />
      )}

      {/* MODAL: ADD EXTERNAL USER WIZARD */}
      {showAddExternalModal && (
        <AddExternalUserWizard
          supplierOrgs={supplierOrgs}
          queues={queues.filter((q) => (q as any).type === 'external' || (q as any).team_type === 'external')}
          onClose={() => setShowAddExternalModal(false)}
          onSuccess={() => {
            setShowAddExternalModal(false);
            fetchAgents();
          }}
          onCreateOrg={createSupplierOrg}
        />
      )}

      {/* DRAWER: SUSPEND AGENT ACCESS */}
      {suspendingAgent && createPortal((
        <div className="fixed inset-x-0 bottom-0 top-14 z-[60] !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-warning/10">
              <div className="flex items-center gap-2 text-warning">
                <AlertCircle className="size-5 shrink-0" />
                <h3 className="font-display font-semibold text-section-title">
                  Suspend Agent Access
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setSuspendingAgent(null)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <p className="font-semibold text-foreground">
                Agent: {`${suspendingAgent.firstName || ''} ${suspendingAgent.lastName || ''}`.trim() || suspendingAgent.email || 'Agent'}
              </p>
              <p className="text-xs text-muted-foreground">
                Suspending an agent temporarily blocks their access to ticket assignments and administrative operations without revoking their team memberships.
              </p>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Reason for Suspension *
                </label>
                <textarea
                  rows={3}
                  placeholder="e.g. Leave of absence, security investigation, policy review..."
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>
            </div>

            <div className="border-t border-border p-4 bg-card flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setSuspendingAgent(null)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteSuspension}
                className="rounded-md bg-warning text-warning-foreground px-3.5 py-1.5 text-xs font-semibold shadow-xs hover:bg-warning/90 transition-all cursor-pointer"
              >
                Confirm Suspension
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* DRAWER: DEACTIVATE & REASSIGN TICKETS */}
      {deactivatingAgent && createPortal((
        <div className="fixed inset-x-0 bottom-0 top-14 z-[60] !m-0 flex justify-end bg-black/50 animate-in fade-in">
          <div className="w-full max-w-md bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
            <div className="p-4 border-b border-border flex items-center justify-between bg-destructive/10">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="size-5 shrink-0" />
                <h3 className="font-display font-semibold text-section-title">
                  Deactivate Agent
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setDeactivatingAgent(null)}
                className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer"
              >
                <X className="size-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
              <p className="font-semibold text-foreground">
                Agent: {`${deactivatingAgent.firstName || ''} ${deactivatingAgent.lastName || ''}`.trim() || deactivatingAgent.email || 'Supplier Agent'}
              </p>
              <p className="text-xs text-muted-foreground">
                Deactivating an agent revokes their access to all tickets and settings immediately. Any open tickets assigned to this agent must be reassigned.
              </p>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Reason for Deactivation *
                </label>
                <input
                  type="text"
                  placeholder="e.g. Departure, role change, vendor contract terminated"
                  value={deactivateReason}
                  onChange={(e) => setDeactivateReason(e.target.value)}
                  className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1">
                  Open Ticket Reassignment Strategy *
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDeactivateReassignMode('queue')}
                    className={cn(
                      "p-2.5 rounded-lg border text-xs font-semibold text-left transition-colors cursor-pointer",
                      deactivateReassignMode === 'queue'
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:bg-muted"
                    )}
                  >
                    Unassign to Queue
                    <div className="text-[10px] font-normal mt-0.5">Tickets return to team unassigned backlog</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeactivateReassignMode('user')}
                    className={cn(
                      "p-2.5 rounded-lg border text-xs font-semibold text-left transition-colors cursor-pointer",
                      deactivateReassignMode === 'user'
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:bg-muted"
                    )}
                  >
                    Reassign to Agent
                    <div className="text-[10px] font-normal mt-0.5">Transfer all active tickets to a colleague</div>
                  </button>
                </div>
              </div>

              {deactivateReassignMode === 'user' && (
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Select Target Agent *
                  </label>
                  <select
                    value={deactivateTargetUserId}
                    onChange={(e) => setDeactivateTargetUserId(e.target.value)}
                    className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  >
                    <option value="">Select an active agent...</option>
                    {agents
                      .filter((a) => a.id !== deactivatingAgent.id && a.status === 'active' && a.isEffective)
                      .map((a) => (
                        <option key={a.userId} value={a.userId}>
                          {a.firstName} {a.lastName} ({a.email})
                        </option>
                      ))}
                  </select>
                </div>
              )}
            </div>
            </div>

            <div className="border-t border-border p-4 bg-card flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeactivatingAgent(null)}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleExecuteDeactivation}
                className="rounded-md bg-destructive text-destructive-foreground px-3.5 py-1.5 text-xs font-semibold shadow-xs hover:bg-destructive/90 transition-all cursor-pointer"
              >
                Confirm Deactivation
              </button>
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DRAWER: AGENT DETAIL DRAWER
// ─────────────────────────────────────────────────────────────────
interface AgentDetailDrawerProps {
  agent: AgentProfile;
  canManage: boolean;
  isSuperAdmin: boolean;
  queues: any[];
  roles: TeamRoleDef[];
  onClose: () => void;
  onRefresh: () => Promise<void>;
  onSuspend: (reason: string) => Promise<void>;
  onReactivate: () => Promise<void>;
  onStartDeactivate: () => void;
}

function AgentDetailDrawer({
  agent,
  canManage,
  isSuperAdmin,
  queues,
  roles,
  onClose,
  onRefresh,
  onSuspend,
  onReactivate,
  onStartDeactivate,
}: AgentDetailDrawerProps) {
  const { agents, setAgentTeams, updateAgent } = useAgentManagementStore();
  const activeAdminCount = agents.filter((a) => a.status === 'active' && a.accessLevel === 'admin').length;
  const isSoleActiveAdmin = agent.status === 'active' && agent.accessLevel === 'admin' && activeAdminCount <= 1;
  const [activeSubTab, setActiveSubTab] = useState<'profile' | 'teams'>('profile');
  const [suspendModalOpen, setSuspendModalOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');

  // Add team membership inline
  const [selectedNewTeamId, setSelectedNewTeamId] = useState('');
  const [selectedNewTeamRole, setSelectedNewTeamRole] = useState('agent');

  const handleAddTeamMembership = async () => {
    if (!selectedNewTeamId) return;
    const current = [...agent.teams];
    const targetQueue = queues.find((q) => String(q.team_id || q.id) === selectedNewTeamId || q.uuid === selectedNewTeamId || q.id === selectedNewTeamId);
    if (current.some((t) => t.teamId === selectedNewTeamId || t.teamUuid === selectedNewTeamId || (targetQueue && isTeamMatch(t, targetQueue)))) {
      toast.error('Agent is already a member of this team');
      return;
    }
    const updated = [
      ...current.map((t) => ({ teamId: t.teamId, teamRoleCode: t.teamRoleCode, isPrimary: t.isPrimary })),
      { teamId: selectedNewTeamId, teamRoleCode: selectedNewTeamRole, isPrimary: current.length === 0 },
    ];
    try {
      await setAgentTeams(agent.id, updated);
      toast.success('Team membership added');
      setSelectedNewTeamId('');
      await onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to add team');
    }
  };

  const handleRemoveTeamMembership = async (teamId: string) => {
    const updated = agent.teams
      .filter((t) => t.teamId !== teamId && t.teamUuid !== teamId)
      .map((t, idx) => ({ teamId: t.teamId, teamRoleCode: t.teamRoleCode, isPrimary: idx === 0 }));
    try {
      await setAgentTeams(agent.id, updated);
      toast.success('Team membership removed');
      await onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to remove team');
    }
  };

  const handleChangeRole = async (teamId: string, roleCode: string) => {
    const updated = agent.teams.map((t) => ({
      teamId: t.teamId,
      teamRoleCode: (t.teamId === teamId || t.teamUuid === teamId) ? roleCode : t.teamRoleCode,
      isPrimary: t.isPrimary,
    }));
    try {
      await setAgentTeams(agent.id, updated);
      toast.success('Team role updated');
      await onRefresh();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to update role');
    }
  };

  return createPortal((
    <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50">
      <div className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col shadow-xl animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="px-5 py-2 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-sm">
              {((agent.firstName?.[0] || agent.email?.[0] || 'A')).toUpperCase()}{agent.lastName?.[0]?.toUpperCase() || ''}
            </div>
            <div>
              <h3 className="font-display font-semibold text-section-title text-foreground">
                {`${agent.firstName || ''} ${agent.lastName || ''}`.trim() || agent.email || 'Agent'}
              </h3>
              <p className="text-xs text-muted-foreground font-mono">{agent.email || '—'}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded text-muted-foreground hover:text-foreground cursor-pointer"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Sub-tab navigation */}
        <div className="flex border-b border-border px-5 gap-4 text-xs font-semibold select-none">
          <button
            type="button"
            onClick={() => setActiveSubTab('profile')}
            className={cn(
              "py-2.5 border-b-2 transition-colors cursor-pointer",
              activeSubTab === 'profile' ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            )}
          >
            Profile &amp; Settings
          </button>
          <button
            type="button"
            onClick={() => setActiveSubTab('teams')}
            className={cn(
              "py-2.5 border-b-2 transition-colors cursor-pointer",
              activeSubTab === 'teams' ? "border-primary text-primary" : "border-transparent text-muted-foreground"
            )}
          >
            Teams ({agent.teams.length})
          </button>
        </div>

        {/* Status Alert Banners */}
        {agent.status === 'suspended' && (
          <div className="mx-5 mt-4 p-3.5 rounded-lg border border-warning/40 bg-warning/10 text-xs flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 text-warning">
              <AlertTriangle className="size-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-foreground">Agent Access Suspended</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  This agent is temporarily suspended. They cannot log in, take ticket assignments, or perform actions.
                </p>
              </div>
            </div>
            {canManage && (
              <button
                type="button"
                onClick={onReactivate}
                className="shrink-0 px-3 py-1 rounded bg-success text-success-foreground text-xs font-semibold hover:bg-success/90 cursor-pointer"
              >
                Reactivate
              </button>
            )}
          </div>
        )}

        {agent.status === 'inactive' && (
          <div className="mx-5 mt-4 p-3.5 rounded-lg border border-destructive/40 bg-destructive/10 text-xs flex items-start justify-between gap-3">
            <div className="flex items-start gap-2.5 text-destructive">
              <ShieldAlert className="size-4 shrink-0 mt-0.5" />
              <div>
                <div className="font-semibold text-foreground">Agent Account Deactivated</div>
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  This account is deactivated. All open tickets were reassigned or unassigned to the queue backlog.
                </p>
              </div>
            </div>
            {canManage && (
              <button
                type="button"
                onClick={onReactivate}
                className="shrink-0 px-3 py-1 rounded bg-success text-success-foreground text-xs font-semibold hover:bg-success/90 cursor-pointer"
              >
                Reactivate
              </button>
            )}
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5 text-xs">
          {activeSubTab === 'profile' && (
            <div className="space-y-4">
              {/* Organization / Profile Info (Read-Only) */}
              <div className="rounded-lg border border-border bg-muted/20 p-4 space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {agent.kind === 'external' ? <Building2 className="size-3.5" /> : <Briefcase className="size-3.5" />}
                  <span>{agent.kind === 'external' ? 'Supplier Organization Details' : 'People Module Profile (Read-Only)'}</span>
                </div>
                {agent.kind === 'external' ? (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] text-muted-foreground">Supplier Organization</div>
                      <div className="font-semibold text-foreground">{agent.supplierOrgName || 'External Vendor'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Access Expiration</div>
                      <div className="font-semibold text-foreground">
                        {agent.accessExpiresAt ? new Date(agent.accessExpiresAt).toLocaleDateString() : 'Permanent / No Expiry'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Account Type</div>
                      <div className="font-semibold text-foreground">External Supplier Agent</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Contact Email</div>
                      <div className="font-semibold text-foreground font-mono">{agent.email || '—'}</div>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[11px] text-muted-foreground">Department</div>
                      <div className="font-semibold text-foreground">{agent.department || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Designation</div>
                      <div className="font-semibold text-foreground">{agent.designation || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Reporting Manager</div>
                      <div className="font-semibold text-foreground">{agent.managerName || '—'}</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-muted-foreground">Contact Phone</div>
                      <div className="font-semibold text-foreground font-mono">{agent.phone || '—'}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Support Specific Properties */}
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Shield className="size-3.5" />
                  <span>Support Specific Attributes</span>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] text-muted-foreground">Access Level</div>
                    <div className="font-semibold text-foreground flex items-center gap-1 mt-0.5">
                      {agent.accessLevel === 'admin' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                          <Shield className="size-3" /> Support Admin
                        </span>
                      ) : agent.accessLevel === 'external' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-info/10 text-info">
                          <Globe className="size-3" /> External Agent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-foreground">
                          Member
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      Assigned by Super Admin in Admin module
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Max Open Tickets</div>
                    <div className="font-semibold text-foreground font-mono mt-0.5">{agent.maxOpenTickets || 10}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Availability Status</div>
                    <div className="font-semibold text-foreground capitalize mt-0.5">{agent.availabilityStatus || 'Offline'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Default Calendar</div>
                    <div className="font-semibold text-foreground font-mono mt-0.5">{agent.defaultCalendarId || 'standard_shift'}</div>
                  </div>
                </div>

                {agent.skills && agent.skills.length > 0 && (
                  <div>
                    <div className="text-[11px] text-muted-foreground mb-1">Skills &amp; Tags</div>
                    <div className="flex flex-wrap gap-1">
                      {agent.skills.map((skill) => (
                        <span key={skill} className="px-2 py-0.5 rounded bg-muted text-[10px] font-mono text-foreground border border-border">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Status Actions */}
              {canManage && (
                <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Lifecycle Actions
                  </div>

                  {isSoleActiveAdmin && (
                    <div className="p-3 rounded-lg border border-warning/30 bg-warning/10 text-xs text-warning flex items-start gap-2">
                      <AlertTriangle className="size-4 shrink-0 mt-0.5" />
                      <span className="text-[11px] text-foreground">
                        This is the only active Support Admin. Cannot suspend or deactivate until another active agent is assigned the Support Admin role.
                      </span>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {agent.status === 'active' && (
                      <button
                        type="button"
                        disabled={isSoleActiveAdmin}
                        onClick={() => setSuspendModalOpen(true)}
                        className={cn(
                          "px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors",
                          isSoleActiveAdmin
                            ? "border-border text-muted-foreground opacity-50 cursor-not-allowed"
                            : "border-warning text-warning hover:bg-warning/10 cursor-pointer"
                        )}
                        title={isSoleActiveAdmin ? "Cannot suspend the only active Support Admin" : "Suspend Access"}
                      >
                        Suspend Access
                      </button>
                    )}

                    {(agent.status === 'suspended' || agent.status === 'inactive') && (
                      <button
                        type="button"
                        onClick={onReactivate}
                        className="px-3 py-1.5 rounded-lg bg-success text-success-foreground text-xs font-semibold hover:bg-success/90 transition-colors cursor-pointer"
                      >
                        Reactivate Agent
                      </button>
                    )}

                    {agent.status !== 'inactive' && (
                      <button
                        type="button"
                        disabled={isSoleActiveAdmin}
                        onClick={onStartDeactivate}
                        className={cn(
                          "px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors",
                          isSoleActiveAdmin
                            ? "border-border text-muted-foreground opacity-50 cursor-not-allowed"
                            : "border-destructive text-destructive hover:bg-destructive/10 cursor-pointer"
                        )}
                        title={isSoleActiveAdmin ? "Cannot deactivate the only active Support Admin" : "Deactivate Agent..."}
                      >
                        Deactivate Agent...
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSubTab === 'teams' && (
            <div className="space-y-4">
              <div className="space-y-2">
                {agent.teams.map((t) => (
                  <div key={t.teamId} className="flex items-center justify-between p-3 rounded-lg border border-border bg-card">
                    <div>
                      <div className="font-semibold text-foreground text-xs flex items-center gap-1.5">
                        <span>{formatTeamName(t.teamName)}</span>
                        {t.isPrimary && (
                          <span className="text-warning text-xs" title="Primary Team">★ Primary</span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono">{t.teamKey}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={(t.teamRoleCode || 'agent').toLowerCase()}
                        disabled={!canManage}
                        onChange={(e) => handleChangeRole(t.teamId, e.target.value)}
                        className="text-xs bg-background border border-input rounded px-2 py-1 text-foreground"
                      >
                        <option value="agent">Support Agent</option>
                        <option value="team_lead">Team Lead</option>
                        <option value="team_manager">Team Manager</option>
                      </select>

                      {canManage && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTeamMembership(t.teamId)}
                          className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                          title="Remove from team"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Team Inline */}
              {canManage && (
                <div className="rounded-lg border border-border bg-muted/20 p-3 space-y-2">
                  <div className="font-semibold text-xs text-foreground">Add to Support Team</div>
                  <div className="flex items-center gap-2">
                    <select
                      value={selectedNewTeamId}
                      onChange={(e) => setSelectedNewTeamId(e.target.value)}
                      className="flex-1 text-xs bg-background border border-input rounded-md px-2 py-1.5 text-foreground"
                    >
                      <option value="">Select team...</option>
                      {queues
                        .filter((q) => !agent.teams.some((t) => isTeamMatch(t, q)))
                        .map((q) => (
                          <option key={q.id || q.queue_key} value={q.team_id || q.id}>
                            {formatTeamName(q.team_name || q.queue_label || q.label)}
                          </option>
                        ))}
                    </select>

                    <select
                      value={selectedNewTeamRole}
                      onChange={(e) => setSelectedNewTeamRole(e.target.value)}
                      className="text-xs bg-background border border-input rounded-md px-2 py-1.5 text-foreground"
                    >
                      <option value="agent">Agent</option>
                      <option value="team_lead">Team Lead</option>
                      <option value="team_manager">Team Manager</option>
                    </select>

                    <button
                      type="button"
                      onClick={handleAddTeamMembership}
                      disabled={!selectedNewTeamId}
                      className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer"
                    >
                      Add
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Drawer: Suspend Reason */}
        {suspendModalOpen && createPortal((
          <div className="fixed inset-x-0 bottom-0 top-14 z-[60] !m-0 flex justify-end bg-black/50 animate-in fade-in">
            <div className="w-full max-w-sm bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between bg-warning/10">
                <h4 className="font-semibold text-foreground text-sm">Suspend Agent Access</h4>
                <button
                  type="button"
                  onClick={() => setSuspendModalOpen(false)}
                  className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent"
                >
                  <X className="size-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
                <p className="text-xs text-muted-foreground">
                  Please enter a reason for suspending {agent.firstName}. They will be unable to log into support tickets until reactivated.
                </p>
                <div>
                  <label className="block text-xs font-semibold text-foreground mb-1">Reason for Suspension</label>
                  <input
                    type="text"
                    placeholder="Reason for suspension..."
                    value={suspendReason}
                    onChange={(e) => setSuspendReason(e.target.value)}
                    className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                  />
                </div>
              </div>
              <div className="border-t border-border p-4 bg-card flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSuspendModalOpen(false)}
                  className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await onSuspend(suspendReason || 'Administrative suspension');
                    setSuspendModalOpen(false);
                  }}
                  className="rounded-md bg-warning text-warning-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-warning/90 shadow-xs"
                >
                  Confirm Suspension
                </button>
              </div>
            </div>
          </div>
        ), document.body)}
      </div>
    </div>
  ), document.body);
}

// ─────────────────────────────────────────────────────────────────
// WIZARD: ADD INTERNAL AGENT
// ─────────────────────────────────────────────────────────────────
interface AddInternalAgentWizardProps {
  queues: any[];
  roles: TeamRoleDef[];
  isSuperAdmin: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function AddInternalAgentWizard({
  queues,
  roles,
  isSuperAdmin,
  onClose,
  onSuccess,
}: AddInternalAgentWizardProps) {
  const { eligibleEmployees, fetchEligibleEmployees, createInternalAgent } = useAgentManagementStore();

  const [step, setStep] = useState<number>(1);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState<EligibleEmployee | null>(null);

  // Configuration form
  const [accessLevel, setAccessLevel] = useState<'admin' | 'member'>('member');
  const [selectedTeams, setSelectedTeams] = useState<Array<{ teamId: string; teamRoleCode: string; isPrimary: boolean }>>([]);
  const [skillsInput, setSkillsInput] = useState('Hardware, Network, Windows, Linux');
  const [maxOpenTickets, setMaxOpenTickets] = useState(15);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchEligibleEmployees();
  }, [fetchEligibleEmployees]);

  useEffect(() => {
    if (selectedEmployee) {
      setAccessLevel(selectedEmployee.accessLevel || 'member');
    }
  }, [selectedEmployee]);

  const filteredEmployees = useMemo(() => {
    if (!employeeSearch.trim()) return eligibleEmployees;
    const q = employeeSearch.toLowerCase();
    return eligibleEmployees.filter((e) =>
      `${e.firstName} ${e.lastName}`.toLowerCase().includes(q) ||
      e.workEmail.toLowerCase().includes(q) ||
      (e.department || '').toLowerCase().includes(q)
    );
  }, [eligibleEmployees, employeeSearch]);

  const toggleTeam = (teamId: string) => {
    if (selectedTeams.some((t) => t.teamId === teamId)) {
      setSelectedTeams(selectedTeams.filter((t) => t.teamId !== teamId));
    } else {
      setSelectedTeams([...selectedTeams, { teamId, teamRoleCode: 'agent', isPrimary: selectedTeams.length === 0 }]);
    }
  };

  const setTeamRole = (teamId: string, roleCode: string) => {
    setSelectedTeams(selectedTeams.map((t) => t.teamId === teamId ? { ...t, teamRoleCode: roleCode } : t));
  };

  const setPrimaryTeam = (teamId: string) => {
    setSelectedTeams(selectedTeams.map((t) => ({ ...t, isPrimary: t.teamId === teamId })));
  };

  const handleCreate = async () => {
    if (!selectedEmployee) return;
    if (selectedTeams.length === 0) {
      toast.error('Please assign at least one support team');
      return;
    }
    setIsSubmitting(true);
    try {
      const skills = skillsInput.split(',').map((s) => s.trim()).filter(Boolean);
      await createInternalAgent({
        employeeId: selectedEmployee.id,
        accessLevel,
        teams: selectedTeams,
        skills,
        maxOpenTickets,
      });
      toast.success(`Agent ${selectedEmployee.firstName} ${selectedEmployee.lastName} added successfully!`);
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to create agent');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
      <div className="w-full max-w-xl bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
          <div>
            <h3 className="font-display font-semibold text-section-title text-foreground">
              Add Internal Support Agent
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Step {step} of 3: {step === 1 ? 'Select Employee' : step === 2 ? 'Access & Teams' : 'Review & Confirm'}
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded text-muted-foreground hover:text-foreground cursor-pointer">
            <X className="size-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {step === 1 && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="size-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search employees by name, email, department..."
                  value={employeeSearch}
                  onChange={(e) => setEmployeeSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-background border border-input rounded-md text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                />
              </div>

              <div className="max-h-80 overflow-y-auto border border-border rounded-lg divide-y divide-border">
                {filteredEmployees.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    No eligible employees found.
                  </div>
                ) : (
                  filteredEmployees.map((emp) => {
                    const isSelected = selectedEmployee?.id === emp.id;
                    const canSelect = emp.hasLogin;
                    return (
                      <div
                        key={emp.id}
                        onClick={() => canSelect && setSelectedEmployee(emp)}
                        className={cn(
                          "p-3 flex items-center justify-between transition-colors",
                          !canSelect ? "opacity-50 bg-muted/20 cursor-not-allowed" : "cursor-pointer hover:bg-muted/40",
                          isSelected && "bg-primary/10 border-l-4 border-l-primary"
                        )}
                      >
                        <div className="flex items-center gap-2.5">
                          <div className="size-7 rounded-full bg-primary/10 text-primary font-bold flex items-center justify-center text-xs">
                            {emp.firstName?.[0]}{emp.lastName?.[0]}
                          </div>
                          <div>
                            <div className="font-semibold text-foreground flex items-center gap-1.5">
                              <span>{emp.firstName} {emp.lastName}</span>
                              {!emp.hasLogin && (
                                <span className="text-[10px] text-destructive bg-destructive/10 px-1 rounded font-medium">
                                  No Auth Account
                                </span>
                              )}
                            </div>
                            <div className="text-[11px] text-muted-foreground font-mono">{emp.workEmail}</div>
                          </div>
                        </div>

                        <div className="text-right">
                          <div className="font-medium text-foreground">{emp.department || '—'}</div>
                          <div className="text-[11px] text-muted-foreground">{emp.designation || 'Staff'}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {/* Access Level (Assigned by Super Admin in Admin module) */}
              <div className="space-y-2">
                <label className="block text-xs font-semibold text-muted-foreground">
                  Support Access Level
                </label>
                <div className="p-3.5 rounded-lg border border-border bg-card flex items-start gap-3">
                  {accessLevel === 'admin' ? (
                    <>
                      <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                        <Shield className="size-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">Support Administrator</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary">
                            Assigned via Admin
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          This user holds the <strong>Support Admin</strong> role assigned by Super Admin in the Admin module. They have administrative access across all support queues, settings, and agent workflows.
                        </p>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="p-2 rounded-lg bg-secondary text-foreground shrink-0 mt-0.5">
                        <UserCheck className="size-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-foreground">Support Member</span>
                          <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted text-muted-foreground">
                            Standard Agent
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                          Standard support agent. Queue visibility and ticket permissions are governed strictly by the assigned teams and team roles configured below.
                        </p>
                      </div>
                    </>
                  )}
                </div>

                <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/40 border border-border/80 text-[11px] text-muted-foreground">
                  <Info className="size-3.5 text-primary shrink-0" />
                  <span>
                    Access levels are assigned and governed by Super Admin in the <strong>Admin &gt; Users</strong> module.
                  </span>
                </div>
              </div>

              {/* Team Assignments */}
              <div>
                <label className="block text-xs font-semibold text-muted-foreground mb-1.5">
                  Assign Support Teams &amp; Roles *
                </label>
                <div className="space-y-2 border border-border rounded-lg p-3 max-h-56 overflow-y-auto">
                  {queues.map((team) => {
                    const assigned = selectedTeams.find((t) => t.teamId === String(team.team_id || team.id));
                    return (
                      <div
                        key={team.id || team.queue_key}
                        className={cn(
                          "flex items-center justify-between p-2 rounded-md transition-colors",
                          assigned ? "bg-primary/5 border border-primary/20" : "hover:bg-muted/30"
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={Boolean(assigned)}
                            onChange={() => toggleTeam(String(team.team_id || team.id))}
                            className="rounded border-input text-primary focus:ring-primary"
                          />
                          <span className="font-medium text-foreground">{formatTeamName(team.team_name || team.queue_label || team.label)}</span>
                        </div>

                        {assigned && (
                          <div className="flex items-center gap-2">
                            <select
                              value={assigned.teamRoleCode}
                              onChange={(e) => setTeamRole(assigned.teamId, e.target.value)}
                              className="text-xs bg-background border border-input rounded px-2 py-1 text-foreground"
                            >
                              <option value="agent">Support Agent</option>
                              <option value="team_lead">Team Lead</option>
                              <option value="team_manager">Team Manager</option>
                            </select>

                            <button
                              type="button"
                              onClick={() => setPrimaryTeam(assigned.teamId)}
                              className={cn(
                                "text-xs px-1.5 py-0.5 rounded cursor-pointer",
                                assigned.isPrimary ? "bg-warning/20 text-warning font-semibold" : "text-muted-foreground hover:text-foreground"
                              )}
                              title="Set as Primary Team"
                            >
                              {assigned.isPrimary ? '★ Primary' : 'Make Primary'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Skills & Capacity */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Skills (comma separated)
                  </label>
                  <input
                    type="text"
                    value={skillsInput}
                    onChange={(e) => setSkillsInput(e.target.value)}
                    className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-foreground mb-1">
                    Max Open Tickets
                  </label>
                  <input
                    type="number"
                    value={maxOpenTickets}
                    onChange={(e) => setMaxOpenTickets(Number(e.target.value))}
                    className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
                  />
                </div>
              </div>
            </div>
          )}

          {step === 3 && selectedEmployee && (
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-4 space-y-3">
                <h4 className="font-semibold text-foreground text-sm">Review Agent Setup</h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <div className="text-[11px] text-muted-foreground">Employee Name</div>
                    <div className="font-semibold text-foreground">{selectedEmployee.firstName} {selectedEmployee.lastName}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Work Email</div>
                    <div className="font-semibold text-foreground font-mono">{selectedEmployee.workEmail}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Department</div>
                    <div className="font-semibold text-foreground">{selectedEmployee.department || '—'}</div>
                  </div>
                  <div>
                    <div className="text-[11px] text-muted-foreground">Access Level</div>
                    <div className="font-semibold text-primary capitalize">{accessLevel}</div>
                  </div>
                </div>

                <div className="border-t border-border pt-3">
                  <div className="text-[11px] text-muted-foreground mb-1">Assigned Teams ({selectedTeams.length})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedTeams.map((t) => {
                      const q = queues.find((x) => String(x.team_id || x.id) === t.teamId);
                      return (
                        <span key={t.teamId} className="px-2 py-0.5 rounded bg-muted text-xs border border-border font-medium">
                          {formatTeamName(q?.team_name || q?.queue_label || t.teamId)} ({t.teamRoleCode}) {t.isPrimary && '★'}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-card flex items-center justify-between">
          {step > 1 ? (
            <button
              type="button"
              onClick={() => setStep(step - 1)}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted cursor-pointer"
            >
              Back
            </button>
          ) : <div />}

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted cursor-pointer"
            >
              Cancel
            </button>

            {step < 3 ? (
              <button
                type="button"
                disabled={step === 1 && !selectedEmployee}
                onClick={() => setStep(step + 1)}
                className="rounded-md bg-primary text-primary-foreground px-3.5 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer shadow-xs"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                disabled={isSubmitting}
                onClick={handleCreate}
                className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer shadow-xs"
              >
                {isSubmitting ? 'Creating...' : 'Create Agent'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// WIZARD: ADD EXTERNAL USER
// ─────────────────────────────────────────────────────────────────
interface AddExternalUserWizardProps {
  supplierOrgs: SupplierOrg[];
  queues: any[];
  onClose: () => void;
  onSuccess: () => void;
  onCreateOrg: (data: any) => Promise<SupplierOrg>;
}

function AddExternalUserWizard({
  supplierOrgs,
  queues,
  onClose,
  onSuccess,
  onCreateOrg,
}: AddExternalUserWizardProps) {
  const { createExternalAgent } = useAgentManagementStore();

  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [showNewOrgForm, setShowNewOrgForm] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgDomain, setNewOrgDomain] = useState('');

  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [accessExpiresAt, setAccessExpiresAt] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 90);
    return d.toISOString().split('T')[0];
  });
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCreateOrgMini = async () => {
    if (!newOrgName.trim()) {
      toast.error('Organisation name is required');
      return;
    }
    try {
      const created = await onCreateOrg({
        name: newOrgName.trim(),
        domain: newOrgDomain.trim() || undefined,
      });
      setSelectedOrgId(created.id);
      setShowNewOrgForm(false);
      toast.success(`Supplier organisation "${created.name}" created`);
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to create organisation');
    }
  };

  const handleCreateExternal = async () => {
    if (!selectedOrgId) {
      toast.error('Please select or create a supplier organisation');
      return;
    }
    if (!contactName.trim() || !contactEmail.trim()) {
      toast.error('Contact name and email are required');
      return;
    }
    if (!selectedTeamId) {
      toast.error('Please select an external-type support team');
      return;
    }

    setIsSubmitting(true);
    try {
      await createExternalAgent({
        supplierOrgId: selectedOrgId,
        name: contactName.trim(),
        email: contactEmail.trim().toLowerCase(),
        phone: contactPhone.trim() || undefined,
        teams: [{ teamId: selectedTeamId, teamRoleCode: 'agent' }],
        accessExpiresAt: accessExpiresAt ? new Date(accessExpiresAt).toISOString() : undefined,
      });
      toast.success(`External user invitation sent to ${contactEmail}`);
      onSuccess();
    } catch (err: any) {
      toast.error(err.response?.data?.message || err.message || 'Failed to invite external agent');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 top-14 z-50 !m-0 flex justify-end bg-black/50 animate-in fade-in">
      <div className="w-full max-w-lg bg-card border-l border-border h-full flex flex-col shadow-2xl animate-in slide-in-from-right duration-200 overflow-hidden">
        <div className="p-4 border-b border-border flex items-center justify-between bg-muted/20">
          <div className="flex items-center gap-2">
            <Globe className="size-5 text-info" />
            <h3 className="font-display font-semibold text-section-title text-foreground">
              Invite External Supplier Agent
            </h3>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer">
            <X className="size-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          {/* Supplier Org */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="font-semibold text-muted-foreground">Supplier Organisation *</label>
              <button
                type="button"
                onClick={() => setShowNewOrgForm(!showNewOrgForm)}
                className="text-[11px] text-primary hover:underline cursor-pointer"
              >
                {showNewOrgForm ? 'Select existing org' : '+ New organisation'}
              </button>
            </div>

            {showNewOrgForm ? (
              <div className="p-3 rounded-lg border border-border bg-muted/20 space-y-2">
                <input
                  type="text"
                  placeholder="Organisation Name (e.g. Dell Support, Cisco Global)"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
                />
                <input
                  type="text"
                  placeholder="Domain (e.g. dell.com)"
                  value={newOrgDomain}
                  onChange={(e) => setNewOrgDomain(e.target.value)}
                  className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
                />
                <button
                  type="button"
                  onClick={handleCreateOrgMini}
                  className="px-3 py-1 rounded bg-primary text-primary-foreground text-xs font-semibold"
                >
                  Save Organisation
                </button>
              </div>
            ) : (
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground"
              >
                <option value="">Select supplier organisation...</option>
                {supplierOrgs.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} {org.domain ? `(${org.domain})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Contact Details */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-muted-foreground mb-1">Contact Full Name *</label>
              <input
                type="text"
                placeholder="Jane Vendor"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
              />
            </div>
            <div>
              <label className="block font-semibold text-muted-foreground mb-1">Contact Work Email *</label>
              <input
                type="email"
                placeholder="jane@supplier.com"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block font-semibold text-muted-foreground mb-1">Phone Number</label>
              <input
                type="text"
                placeholder="+1 555-0199"
                value={contactPhone}
                onChange={(e) => setContactPhone(e.target.value)}
                className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
              />
            </div>
            <div>
              <label className="block font-semibold text-muted-foreground mb-1">Access Expiry Date *</label>
              <input
                type="date"
                value={accessExpiresAt}
                onChange={(e) => setAccessExpiresAt(e.target.value)}
                className="w-full text-xs px-3 py-1.5 bg-background border border-input rounded-md text-foreground"
              />
            </div>
          </div>

          {/* External Team */}
          <div>
            <label className="block font-semibold text-muted-foreground mb-1">
              External Support Team *
            </label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              className="w-full text-xs px-3 py-2 bg-background border border-input rounded-md text-foreground"
            >
              <option value="">Select external team...</option>
              {queues.map((q) => (
                <option key={q.id || q.queue_key} value={q.team_id || q.id}>
                  {formatTeamName(q.team_name || q.queue_label || q.label)} (External)
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground mt-1">
              External agents may belong strictly to teams flagged as external supplier teams.
            </p>
          </div>

          <div className="p-3 rounded-lg bg-info/5 border border-info/20 text-info text-xs">
            An email invitation will be automatically dispatched. The external agent status will remain &quot;Invited&quot; until they accept and activate their account.
          </div>
        </div>

        <div className="border-t border-border p-4 bg-card flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={handleCreateExternal}
            className="rounded-md bg-primary text-primary-foreground px-4 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 cursor-pointer shadow-xs"
          >
            {isSubmitting ? 'Sending Invite...' : 'Send Invitation'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RoleCard({
  role,
  isSuperAdmin,
  onTogglePermission,
}: {
  role: TeamRoleDef;
  isSuperAdmin: boolean;
  onTogglePermission: (role: TeamRoleDef, code: string, granted: boolean) => void;
}) {
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState<'all' | 'granted'>('granted');

  const grantedCount = (role.permissions || []).filter((p) => p.granted).length;

  const filteredPerms = useMemo(() => {
    return (role.permissions || []).filter((p) => {
      if (filterMode === 'granted' && !p.granted) return false;
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const mName = (p.name || '').toLowerCase().includes(q);
        const mCode = (p.code || '').toLowerCase().includes(q);
        const mCategory = (p.category || '').toLowerCase().includes(q);
        if (!mName && !mCode && !mCategory) return false;
      }
      return true;
    });
  }, [role.permissions, filterMode, search]);

  return (
    <div className="rounded-lg border border-border bg-card p-4 space-y-3 flex flex-col justify-between shadow-2xs">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Award className="size-4 text-primary" />
            <h4 className="font-semibold text-foreground text-sm">{role.name}</h4>
          </div>
          <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground font-mono">
            {role.code}
          </span>
        </div>
        <p className="text-xs text-muted-foreground min-h-[32px] line-clamp-2 leading-relaxed">
          {role.description || 'System support team role.'}
        </p>

        {/* Filter Bar */}
        <div className="space-y-2 pt-2 border-t border-border">
          <div className="flex items-center justify-between gap-1 text-xs">
            <div className="font-semibold text-foreground text-[11px] uppercase tracking-wider">
              Permissions ({grantedCount})
            </div>
            <div className="inline-flex rounded-md border border-border p-0.5 text-[10px]">
              <button
                type="button"
                onClick={() => setFilterMode('granted')}
                className={cn(
                  "px-1.5 py-0.5 rounded transition-colors cursor-pointer",
                  filterMode === 'granted' ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
                )}
              >
                Granted ({grantedCount})
              </button>
              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className={cn(
                  "px-1.5 py-0.5 rounded transition-colors cursor-pointer",
                  filterMode === 'all' ? "bg-primary text-primary-foreground font-semibold" : "text-muted-foreground hover:text-foreground"
                )}
              >
                All ({role.permissions?.length || 0})
              </button>
            </div>
          </div>

          <div className="relative">
            <Search className="size-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search permissions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-6 pr-2 py-1 text-[11px] bg-background border border-input rounded text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
      </div>

      {/* Permissions List */}
      <div className="max-h-72 overflow-y-auto space-y-1 pr-1 border-t border-border pt-2 text-xs">
        {filteredPerms.length === 0 ? (
          <div className="py-6 text-center text-muted-foreground text-[11px]">
            No matching permissions found.
          </div>
        ) : (
          filteredPerms.map((perm) => (
            <div
              key={perm.code}
              onClick={() => isSuperAdmin && onTogglePermission(role, perm.code, Boolean(perm.granted))}
              className={cn(
                "flex items-center justify-between py-1 px-2 rounded text-xs border transition-colors select-none",
                perm.granted
                  ? "bg-primary/5 border-primary/20 text-foreground"
                  : "bg-muted/10 border-border/50 text-muted-foreground opacity-75",
                isSuperAdmin ? "cursor-pointer hover:border-primary/50" : "cursor-default"
              )}
              title={isSuperAdmin ? `Click to ${perm.granted ? 'revoke' : 'grant'} permission` : undefined}
            >
              <div className="flex items-center gap-1.5 truncate">
                {perm.granted ? (
                  <CheckCircle2 className="size-3 text-success shrink-0" />
                ) : (
                  <span className="size-3 rounded-full border border-muted-foreground/30 shrink-0" />
                )}
                <span className={cn("truncate text-[11px]", perm.granted ? "font-medium" : "")}>
                  {perm.name || perm.code}
                </span>
              </div>
              <span className="text-[9px] font-mono text-muted-foreground px-1 bg-muted rounded shrink-0 ml-1">
                {perm.scope}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
